import { useCallback, useEffect, useRef, useState } from 'react';
import { MicroAppClient } from '@byte-coach/hutbot-mapp-sdk-js';
import { redeemMappToken } from '../api';
import NetworkManager, { SESSION_EXPIRED_EVENT } from '../api/NetworkManager';
import { environmentConfig, currentEnvironment } from '../config/environment';
import {
  Environment,
  getEnvironmentConfig,
  getOrgFromBrandCountry,
} from '@byte-store-control/config';
import type { User } from '@byte-store-control/shared';

export enum AUTH_STATE {
  INITIAL = 'INITIAL',
  AUTHORIZE = 'AUTHORIZE',
  VALIDATE = 'VALIDATE',
}

interface MAppAuthQueryParams {
  mappToken?: string;
  environment?: string;
  // Dev bypass params - allows skipping SSO redirect entirely
  devBypass?: string;
  accessToken?: string;
}

// Session storage keys for auth persistence (survives page refresh within tab)
// Note: sessionStorage is used instead of cookies because:
// 1. CloudFront strips cookies before forwarding to API Gateway
// 2. All API auth uses Authorization header via NetworkManager
// 3. sessionStorage provides page-refresh persistence without security concerns of cookies
const AUTH_STORAGE_KEYS = {
  MAPP_TOKEN: 'mapp_token',
  ACCESS_TOKEN: 'access_token',
  USER_DATA: 'user_data',
  ENVIRONMENT: 'mapp_environment',
  IS_IN_MAPP: 'is_in_mapp', // Tracks if running inside native MApp container
  BRAND: 'mapp_brand',
} as const;

export function useAuth() {
  // Do not cache URLSearchParams in a ref: redeemable `mappToken` is single-use.
  // If we keep an old query param around, we'll repeatedly attempt to redeem a stale token and loop auth.
  const initialSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(initialSearchParams.entries()) as MAppAuthQueryParams;

  // Check for dev bypass mode early - before any other logic
  const hasDevBypass = params.devBypass === 'true' && params.accessToken;

  // Normalize environment value from MApp SDK to our supported environments
  const normalizeEnvironment = (env: string | undefined | null): Environment => {
    if (!env) {
      // SDK sometimes returns undefined - default to build environment
      return currentEnvironment;
    }
    const envLower = env.toLowerCase();
    // Local dev uses the "local" environment, even if the SDK returns "dev".
    // This ensures the backend uses the local MApp registration (store-admin-local).
    if (envLower === 'dev' || envLower === 'development') {
      return currentEnvironment === 'local' ? 'local' : 'dev';
    }
    // Map 'stage' to 'staging'
    if (envLower === 'stage') {
      return 'staging';
    }
    // Map 'prod' to 'production'
    if (envLower === 'prod') {
      return 'production';
    }
    // If it's already a valid environment, return it
    if (
      envLower === 'local' ||
      envLower === 'dev' ||
      envLower === 'staging' ||
      envLower === 'production'
    ) {
      return envLower as Environment;
    }
    // Default to local if unknown
    console.warn(`Unknown environment '${env}' received, defaulting to 'local'`);
    return 'local';
  };

  const resolveAuthEnvironment = (envParam?: string | null): Environment => {
    if (envParam) {
      return normalizeEnvironment(envParam);
    }
    const storedEnv = sessionStorage.getItem(AUTH_STORAGE_KEYS.ENVIRONMENT);
    return normalizeEnvironment(storedEnv) || currentEnvironment;
  };

  const getMAppClient = (envParam?: string | null) => {
    const resolvedEnv = resolveAuthEnvironment(envParam);
    const authConfig = getEnvironmentConfig(resolvedEnv);
    return new MicroAppClient({
      mappId: authConfig.APP_MAPP_ID,
      authURL: authConfig.APP_MAPP_AUTH_URL,
    });
  };

  NetworkManager.setEnvironment(currentEnvironment);

  const clientRef = useRef(getMAppClient(params.environment));

  // If dev bypass is present, treat it like having a token to validate
  const [authState, setAuthState] = useState(
    params.mappToken || hasDevBypass ? AUTH_STATE.VALIDATE : AUTH_STATE.INITIAL
  );
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | undefined>(undefined);
  // Track if running inside native MApp container (vs standalone browser)
  const [isInMApp, setIsInMApp] = useState(() => {
    return sessionStorage.getItem(AUTH_STORAGE_KEYS.IS_IN_MAPP) === 'true';
  });

  useEffect(() => {
    if (user) {
      setIsLoading(false);
    }
  }, [user]);

  const cleanAuthToken = useCallback(() => {
    NetworkManager.clearAuthorizationToken();
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.MAPP_TOKEN);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.ENVIRONMENT);
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.IS_IN_MAPP);
    setIsInMApp(false);
  }, []);

  const logout = useCallback(async () => {
    cleanAuthToken();
    window.location.href = environmentConfig.APP_MAPP_AUTH_URL;
  }, [cleanAuthToken]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const updatePermissions = useCallback(async () => {
    try {
      const urlSearchParams = new URLSearchParams(window.location.search);

      const envParam =
        urlSearchParams.get('environment') ||
        sessionStorage.getItem(AUTH_STORAGE_KEYS.ENVIRONMENT) ||
        'local';
      const environment = normalizeEnvironment(envParam);
      NetworkManager.setEnvironment(environment);

      // Check for dev bypass mode - allows passing JWT directly without SSO
      const devBypass = urlSearchParams.get('devBypass');
      const bypassToken = urlSearchParams.get('accessToken');

      if (devBypass === 'true' && bypassToken) {
        // Clear any old session data first to ensure clean state
        sessionStorage.removeItem(AUTH_STORAGE_KEYS.MAPP_TOKEN);
        sessionStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
        sessionStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
        sessionStorage.removeItem(AUTH_STORAGE_KEYS.ENVIRONMENT);

        // Decode the JWT to extract user info
        try {
          const tokenParts = bypassToken.split('.');
          if (tokenParts.length !== 3) {
            throw new Error('Invalid JWT format - expected 3 parts');
          }

          const payload = JSON.parse(atob(tokenParts[1]));

          const hutbotUser =
            typeof payload.hutbot_user === 'string'
              ? JSON.parse(payload.hutbot_user)
              : payload.hutbot_user;
          const identity = payload.identities?.[0] || {};

          // Check if token is expired
          if (payload.exp && Date.now() >= payload.exp * 1000) {
            throw new Error('Bypass token has expired');
          }

          const brand = hutbotUser?.userPrimaryBrand || 'TBC';
          const country = hutbotUser?.userCountryIsoCode || 'GBR';
          const { org, displayName: orgDisplayName } = getOrgFromBrandCountry(brand, country);

          const userData = {
            yumId: identity.userId || 'unknown',
            firstName: hutbotUser?.firstName || 'Dev',
            lastName: hutbotUser?.lastName || 'User',
            email: `${identity.userId || 'dev'}@yum.com`,
            roles: hutbotUser?.userRoleGroups || [],
            permissions: payload.scope || [],
            org,
            orgDisplayName,
            brand,
            country,
          };

          NetworkManager.setAuthorizationToken(bypassToken);
          setUser(userData);
          sessionStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, bypassToken);
          sessionStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
          sessionStorage.setItem(AUTH_STORAGE_KEYS.ENVIRONMENT, environment);

          // Clean URL params
          window.history.replaceState({}, document.title, window.location.pathname);
          setIsLoading(false);
          return;
        } catch {
          // Failed to decode bypass token - fall through to normal auth flow
        }
      }

      // Check for cached session first (from previous auth or devBypass)
      const accessToken = sessionStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
      const cachedUser = sessionStorage.getItem(AUTH_STORAGE_KEYS.USER_DATA);

      if (accessToken && cachedUser) {
        NetworkManager.setAuthorizationToken(accessToken);
        const cachedBrand = sessionStorage.getItem(AUTH_STORAGE_KEYS.BRAND);
        if (cachedBrand) {
          NetworkManager.setBrandId(cachedBrand);
        }
        const userData = JSON.parse(cachedUser);
        setUser(userData);
        setIsLoading(false);
        return;
      }

      // Capture brand from URL params (browser auth flow) or sessionStorage
      const brand = urlSearchParams.get('brand') || sessionStorage.getItem(AUTH_STORAGE_KEYS.BRAND);
      if (brand) {
        sessionStorage.setItem(AUTH_STORAGE_KEYS.BRAND, brand);
        NetworkManager.setBrandId(brand);
      }

      // Normal flow: Get the MApp token (either from URL params or sessionStorage)
      const mappToken =
        urlSearchParams.get('mappToken') || sessionStorage.getItem(AUTH_STORAGE_KEYS.MAPP_TOKEN);

      if (!mappToken) {
        throw new Error('MApp token is required');
      }

      const permissionResp = await redeemMappToken(mappToken);

      if (permissionResp.status && permissionResp.accessToken) {
        // IMPORTANT: Set the JWT access token, not the MApp token
        NetworkManager.setAuthorizationToken(permissionResp.accessToken);
        setUser(permissionResp.data);
        sessionStorage.setItem(AUTH_STORAGE_KEYS.MAPP_TOKEN, mappToken);
        sessionStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, permissionResp.accessToken);
        sessionStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(permissionResp.data));
        sessionStorage.setItem(AUTH_STORAGE_KEYS.ENVIRONMENT, environment);
        setIsLoading(false);
      } else {
        throw new Error('Failed to get permissions');
      }
    } catch {
      cleanAuthToken();
      setAuthState(AUTH_STATE.AUTHORIZE);
    }
  }, [cleanAuthToken]);

  useEffect(() => {
    switch (authState) {
      case AUTH_STATE.INITIAL:
      case AUTH_STATE.VALIDATE:
        updatePermissions();
        break;
      case AUTH_STATE.AUTHORIZE:
        clientRef.current.authenticate();
        break;
    }
  }, [authState, updatePermissions]);

  useEffect((): (() => void) => {
    const onReceivedRedeemableToken = (data: {
      mappToken: string;
      environment?: string;
      brand?: string;
    }) => {
      const { mappToken, environment, brand } = data;
      // SDK may return undefined environment - default to current build environment
      const normalizedEnv = normalizeEnvironment(environment || currentEnvironment);
      try {
        sessionStorage.setItem(AUTH_STORAGE_KEYS.MAPP_TOKEN, mappToken);
        sessionStorage.setItem(AUTH_STORAGE_KEYS.ENVIRONMENT, normalizedEnv);
        if (brand) {
          sessionStorage.setItem(AUTH_STORAGE_KEYS.BRAND, brand);
          NetworkManager.setBrandId(brand);
        }
        // Mark that we're running inside the native MApp container
        sessionStorage.setItem(AUTH_STORAGE_KEYS.IS_IN_MAPP, 'true');
        setIsInMApp(true);
        const urlSearchParams = new URLSearchParams(window.location.search);
        urlSearchParams.delete('mappToken');
        urlSearchParams.delete('environment');
        window.history.pushState({}, document.title, window.location.pathname);
      } catch {
        // Failed to save token to sessionStorage
      }
      setAuthState(AUTH_STATE.INITIAL);
    };

    const unsub = clientRef.current.subscribe({
      onReceivedRedeemableToken,
      onError: () => {},
    });

    return unsub as () => void;
  }, []);

  // Listen for session expiration events from NetworkManager
  useEffect(() => {
    const handleSessionExpired = () => {
      logout();
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [logout]);

  const accessToken = sessionStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN) || '';
  const environment = sessionStorage.getItem(AUTH_STORAGE_KEYS.ENVIRONMENT) || 'local';

  return {
    isLoading,
    user,
    logout,
    updatePermissions,
    accessToken, // Return the actual JWT access token, not the MApp token
    environment,
    isInMApp, // True when running inside native MApp container
  };
}
